const MY_MAPS_ID = "18d2PEpQV8siZdUX2lGeP27qeZp63pN8";

export const MY_MAPS_KML_URL = `https://www.google.com/maps/d/kml?mid=${MY_MAPS_ID}&forcekml=1`;

const getChildText = (node, tagName) => {
  const child = node.getElementsByTagNameNS("*", tagName)[0];
  return child ? child.textContent.trim() : "";
};

const normalizeKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\u0111/g, "d")
    .replace(/\u0110/g, "D")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const getDataValue = (placemark, dataName) => {
  const normalizedDataName = normalizeKey(dataName);
  const dataNodes = Array.from(placemark.getElementsByTagNameNS("*", "Data"));
  const dataNode = dataNodes.find(
    (node) => normalizeKey(node.getAttribute("name")) === normalizedDataName,
  );

  return dataNode ? getChildText(dataNode, "value") : "";
};

const parseCoordinateValue = (value) => {
  const text = String(value || "").trim();
  const decimal = Number(text);

  if (!Number.isNaN(decimal)) {
    return decimal;
  }

  const dmsMatch = text.match(/(\d+)\u00b0(\d+)'([\d.]+)"?\s*([NSEW])?/i);

  if (!dmsMatch) {
    return null;
  }

  const [, degrees, minutes, seconds, direction = ""] = dmsMatch;
  const coordinate =
    Number(degrees) + Number(minutes) / 60 + Number(seconds) / 3600;
  const sign = /[SW]/i.test(direction) ? -1 : 1;

  return coordinate * sign;
};

const getPointCoordinates = (placemark) => {
  const coordinatesText = getChildText(placemark, "coordinates");

  if (!coordinatesText) {
    return null;
  }

  const [lngText, latText] = coordinatesText.split(",").map((item) => item.trim());
  const lat = Number(latText);
  const lng = Number(lngText);

  if (Number.isNaN(lat) || Number.isNaN(lng)) {
    return null;
  }

  return { lat, lng };
};

const normalizeText = (value) =>
  normalizeKey(value).replace(/[^a-z0-9 ]/g, "").trim();

const hasSameNameTokens = (nameA, nameB) => {
  const tokensA = new Set(normalizeText(nameA).split(" ").filter(Boolean));
  const tokensB = new Set(normalizeText(nameB).split(" ").filter(Boolean));

  if (!tokensA.size || tokensA.size !== tokensB.size) {
    return false;
  }

  return [...tokensA].every((token) => tokensB.has(token));
};

const findFallbackPhone = (showroom, fallbackShowrooms) => {
  const normalizedName = normalizeText(showroom.name);
  const normalizedAddress = normalizeText(showroom.address);

  const matched = fallbackShowrooms.find((fallback) => {
    const fallbackName = normalizeText(fallback.name);
    const fallbackAddress = normalizeText(fallback.address);
    const sameLocation =
      Math.abs(Number(fallback.lat) - showroom.lat) < 0.0005 &&
      Math.abs(Number(fallback.lng) - showroom.lng) < 0.0005;

    return (
      sameLocation ||
      hasSameNameTokens(showroom.name, fallback.name) ||
      normalizedName.includes(fallbackName) ||
      fallbackName.includes(normalizedName) ||
      normalizedAddress.includes(fallbackAddress) ||
      fallbackAddress.includes(normalizedAddress)
    );
  });

  return matched ? matched.phone : "";
};

export const parseMyMapsKml = (kmlText, fallbackShowrooms = []) => {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(kmlText, "application/xml");
  const parserError = xmlDoc.getElementsByTagName("parsererror")[0];

  if (parserError) {
    throw new Error("Khong the doc du lieu KML");
  }

  const placemarks = Array.from(xmlDoc.getElementsByTagNameNS("*", "Placemark"));

  return placemarks
    .map((placemark, index) => {
      const pointCoordinates = getPointCoordinates(placemark);
      const lat =
        pointCoordinates?.lat || parseCoordinateValue(getDataValue(placemark, "kinh do"));
      const lng =
        pointCoordinates?.lng || parseCoordinateValue(getDataValue(placemark, "vi do"));

      if (lat === null || lng === null) {
        return null;
      }

      const showroom = {
        name: getChildText(placemark, "name"),
        lat,
        lng,
        address: getDataValue(placemark, "dia chi xuong") || getChildText(placemark, "address"),
        phone: "",
        source: "kml",
        order: index + 1,
      };

      return {
        ...showroom,
        phone: findFallbackPhone(showroom, fallbackShowrooms),
      };
    })
    .filter(Boolean);
};

export const fetchMyMapsShowrooms = async (fallbackShowrooms = []) => {
  const response = await fetch(MY_MAPS_KML_URL);

  if (!response.ok) {
    throw new Error(`Khong the tai KML: ${response.status}`);
  }

  const kmlText = await response.text();
  return parseMyMapsKml(kmlText, fallbackShowrooms);
};
